/**
 * RepeatableGroupRenderer
 *
 * Renders a field-group placeholder inside a form. Uses react-hook-form's
 * `useFieldArray` to manage an array of instances under `fieldData[group_key]`.
 * Looks up the group's children (com_fields) from the field-groups API and
 * renders each instance as a card with the same field types FormInstanceEditor
 * supports for top-level fields.
 *
 * Storage shape:
 *   field_data["agency_contact"] = [
 *     { agency_name: "...", contact_phone: "..." },
 *     { agency_name: "...", contact_phone: "..." }
 *   ]
 *
 * The "group key" used for storage is the form-builder-supplied
 * `field.field_id` (which mirrors the group's `name` in the v1 design).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch, Controller } from 'react-hook-form';
import { Plus, Trash2, ChevronDown, ChevronRight, Sparkles, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { fieldGroupsAPI, FieldGroup, FieldGroupTemplate } from '@/services/api/fieldGroups';
import { FormFieldSchema } from '@/services/api/forms';
import { normalizeOptions } from './fieldOptions';
import type { ComputedFormula } from '@/services/api/members';
import {
  describeRule,
  evaluateSameRecord,
  formatDerived,
  isCrossRecord,
  parseDateValue,
  resolveChildDerivation as resolveDerivation,
  resolveDobSourceName as resolveDobName,
  type ChildDerivation,
} from '@/utils/derivedFields';

type ChildField = FieldGroup['children'][number];

// ---------------------------------------------------------------------------
// Derived fields inside a repeatable group
//
// A group child can be CALCULATED — its value comes from a rule, not from the
// user — and each instance is its own record, so "Age of Household Member" is
// derived from the DOB in the SAME row.
//
// Two ways a child gets there, in priority order:
//
//  1. **A configured rule** (`computed_formula`, TRUS-383/392). Authoritative:
//     the admin declared what this field is calculated from, in Field
//     Configuration. Works for any calculation and any field name.
//  2. **The legacy DOB/age name heuristic** (TRUS-226), kept as a fallback for
//     orgs that have not declared a rule yet. Matching an integer "age" child
//     against a lone date-of-birth sibling is a guess, which is exactly why the
//     rule supersedes it — but removing it would regress every org relying on
//     it today.
//
// The value is computed client-side even though the backend now derives it too:
// while someone is typing a date of birth, the age has to update before
// anything is saved.
//
// The pure predicates live in utils/derivedFields so they are unit-testable —
// importing this module pulls in the API layer, which touches localStorage at
// module scope. Re-exported here for existing callers.
// ---------------------------------------------------------------------------
export {
  fieldLabel,
  isDobField,
  isAgeField,
  resolveDobSourceName,
  resolveChildDerivation,
} from '@/utils/derivedFields';

interface RepeatableGroupRendererProps {
  field: FormFieldSchema & { field_group_id?: number | null };
  disabled?: boolean;
  // When true, each existing instance renders as a one-line summary row
  // with click-to-expand. Newly appended instances auto-expand for entry.
  // Defaults to false to preserve form-builder behavior.
  compactMode?: boolean;
}

// Admin-configured summary picker (preferred) — resolves the ordered
// `summary_column_field_ids` list against `children` and drops any ids
// the org has since removed. If the result is empty, fall back to the
// heuristic below.
function pickSummaryChildrenFromAdmin(
  children: ChildField[],
  fieldIds: number[] | null | undefined,
): ChildField[] | null {
  if (!fieldIds || fieldIds.length === 0) return null;
  const byId = new Map<number, ChildField>(children.map((c) => [c.field_id, c]));
  const picks: ChildField[] = [];
  for (const id of fieldIds) {
    const c = byId.get(id);
    if (c) picks.push(c);
  }
  return picks.length ? picks : null;
}

// Heuristic picker for the 3 most-diagnostic summary columns of a group:
// 1) first text/string field (the descriptive title)
// 2) first select / status-ish field (state indicator)
// 3) first date / datetime field (when it happened or is due)
// Falls back to first three children in order if the picks don't fill out.
function pickSummaryChildrenHeuristic(children: ChildField[]): ChildField[] {
  const picks: ChildField[] = [];
  const used = new Set<string>();
  const pickFirst = (pred: (c: ChildField) => boolean) => {
    const c = children.find((c) => !used.has(c.name) && pred(c));
    if (c) { picks.push(c); used.add(c.name); }
  };
  pickFirst((c) => c.type === 'str');
  pickFirst((c) => c.type === 'select' || c.type === 'radio');
  pickFirst((c) => c.type === 'date' || c.type === 'datetime' || c.type === 'fixed_date');
  for (const c of children) {
    if (picks.length >= 3) break;
    if (used.has(c.name)) continue;
    if (c.type === 'field_group') continue;
    picks.push(c); used.add(c.name);
  }
  return picks.slice(0, 3);
}

/**
 * Parse a stored date-field value — an ISO / `YYYY-MM-DD` string, an epoch
 * number, or a numeric string — into a Date. Returns null when it isn't usable.
 */
function parseSummaryDate(v: any): Date | null {
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    // Plain calendar dates (YYYY-MM-DD) are built as local dates so the day
    // doesn't shift across timezones (matches utils/dateTime formatDateOnly).
    const ymd = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (/^\d+$/.test(v)) return new Date(Number(v));
    return new Date(v);
  }
  return null;
}

export function formatSummaryValue(v: any, type?: string): string {
  if (v === undefined || v === null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  // Date-typed children must render as a formatted date, not the raw stored
  // value (an epoch number or ISO string would otherwise print as digits).
  if (type === 'date' || type === 'fixed_date' || type === 'datetime') {
    const d = parseSummaryDate(v);
    if (d && !isNaN(d.getTime())) {
      return type === 'datetime' ? d.toLocaleString() : d.toLocaleDateString();
    }
  }
  return String(v);
}

export const RepeatableGroupRenderer: React.FC<RepeatableGroupRendererProps> = ({
  field,
  disabled,
  compactMode = false,
}) => {
  const { control, register, formState } = useFormContext();
  const groupKey = field.field_id;
  const { fields, append, remove } = useFieldArray({ control, name: groupKey });

  const [group, setGroup] = useState<FieldGroup | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!field.field_group_id) {
      setLoadError('Group placeholder is missing field_group_id');
      return;
    }
    fieldGroupsAPI
      .get(field.field_group_id)
      .then((g) => {
        if (cancelled) return;
        setGroup(g);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.detail || err?.message || 'Failed to load group');
      });
    return () => {
      cancelled = true;
    };
  }, [field.field_group_id]);

  // Seed with one empty instance if the group is repeatable and empty (or with
  // min_instances rows if specified). Run once when the group definition arrives.
  useEffect(() => {
    if (!group) return;
    if (fields.length > 0) return;
    const minRows = group.is_repeatable ? Math.max(group.min_instances ?? 0, 0) : 1;
    const seed = minRows > 0 ? minRows : group.is_repeatable ? 0 : 1;
    for (let i = 0; i < seed; i++) {
      append(emptyInstance(group.children), { shouldFocus: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  // -------------------------------------------------------------------------
  // Hooks below must stay above the early returns — React's rules-of-hooks
  // requires a stable hook count across renders. group/loadError can flip
  // null→loaded, so we register every hook unconditionally and guard their
  // bodies with `!group` checks instead of skipping the hook.
  // -------------------------------------------------------------------------

  // Per-instance expand/collapse state (compact mode only). Initial keys reflect
  // any instances seeded by useFieldArray's first effect; freshly-appended rows
  // are auto-expanded so the user can fill them in immediately.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // After append, useFieldArray assigns ids on the next render. We mark
  // "expand the latest row" and the effect below applies it once the new
  // rhfField.id is available.
  const [pendingExpand, setPendingExpand] = useState<boolean>(false);
  useEffect(() => {
    if (!pendingExpand || fields.length === 0) return;
    const last = fields[fields.length - 1];
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.add(last.id);
      return next;
    });
    setPendingExpand(false);
  }, [pendingExpand, fields]);
  const summaryChildren = useMemo(() => {
    if (!group) return [] as ChildField[];
    return (
      pickSummaryChildrenFromAdmin(group.children, group.summary_column_field_ids) ||
      pickSummaryChildrenHeuristic(group.children)
    );
  }, [group]);

  // Name of the DOB field an "age" child derives from (null when the group has
  // no unambiguous DOB/age pair). Recomputed only when the group definition
  // changes. See isAgeField / resolveDobSourceName above (TRUS-226).
  const dobSourceName = useMemo(
    () => (group ? resolveDobName(group.children) : null),
    [group],
  );

  // Templates power the "+ Add ▾" split button. Loaded lazily once the
  // group resolves. Empty list → render the plain "+ Add" button as before.
  const [templates, setTemplates] = useState<FieldGroupTemplate[]>([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  useEffect(() => {
    if (!field.field_group_id) return;
    let cancelled = false;
    fieldGroupsAPI
      .listTemplates(field.field_group_id)
      .then((rows) => { if (!cancelled) setTemplates(rows); })
      .catch(() => { /* templates are optional — ignore failures */ });
    return () => { cancelled = true; };
  }, [field.field_group_id]);

  // Bulk-select state (compact mode only). Keyed by rhfField.id so it
  // survives reorders. Bulk status uses group.status_field_id when set.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Whenever fields change shape (remove etc.), drop ids no longer present.
  useEffect(() => {
    const live = new Set(fields.map((f) => f.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => { if (live.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [fields]);

  // useFormContext is a hook — must be called unconditionally too. Grabbing
  // setValue separately here keeps it next to the bulk-status helpers below.
  const { setValue } = useFormContext();
  const statusChild = useMemo<ChildField | null>(() => {
    if (!group?.status_field_id) return null;
    return group.children.find((c) => c.field_id === group.status_field_id) || null;
  }, [group]);

  // -------------------------------------------------------------------------
  // Early returns now live AFTER every hook above. Non-hook helpers below.
  // -------------------------------------------------------------------------

  if (loadError) {
    return (
      <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3 rounded">
        Field group failed to load: {loadError}
      </div>
    );
  }

  if (!group) {
    return <div className="text-sm text-gray-500 py-2">Loading {field.label}…</div>;
  }

  const atMax =
    group.max_instances != null && fields.length >= group.max_instances;
  const atMin =
    group.min_instances != null && fields.length <= group.min_instances;
  const addLabel =
    group.add_button_label?.trim() || `+ Add another ${group.friendly_name.toLowerCase()}`;

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Translate selected rhfField.ids → form-array indices (compact mode use).
  const selectedIndices = (): number[] => {
    const out: number[] = [];
    fields.forEach((f, i) => { if (selectedIds.has(f.id)) out.push(i); });
    return out;
  };

  const bulkRemove = () => {
    const idxs = selectedIndices().sort((a, b) => b - a); // back-to-front
    idxs.forEach((i) => remove(i));
    clearSelection();
  };

  const bulkSetStatus = (value: string) => {
    if (!statusChild) return;
    selectedIndices().forEach((i) => {
      setValue(`${groupKey}.${i}.${statusChild.name}`, value, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    clearSelection();
  };

  const applyTemplate = (tpl: FieldGroupTemplate) => {
    if (!group) return;
    // Build a fresh empty instance, then layer the template values on top.
    const base = emptyInstance(group.children);
    append({ ...base, ...(tpl.values_json || {}) }, { shouldFocus: true });
    if (compactMode) setPendingExpand(true);
    setShowTemplateMenu(false);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="font-medium">{field.label || group.friendly_name}</Label>
        {group.description && (
          <p className="text-xs text-gray-500 mt-1">{group.description}</p>
        )}
      </div>

      {fields.length === 0 && (
        <div className="text-sm text-gray-500 py-2">
          No entries yet. Click "{addLabel}" to add one.
        </div>
      )}

      {/* Bulk action bar — visible in compact mode only when ≥1 row selected.
          Mark-status actions show only when group has a configured status_field_id. */}
      {compactMode && selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2 text-sm">
          <div className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <CheckSquare className="w-4 h-4" />
            <span>{selectedIds.size} selected</span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-blue-700 dark:text-blue-300 hover:underline"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {statusChild && (
              <>
                <Button type="button" size="sm" variant="outline" onClick={() => bulkSetStatus('completed')}>
                  Mark complete
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => bulkSetStatus('deferred')}>
                  Defer
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => bulkSetStatus('archived')}>
                  Archive
                </Button>
              </>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={bulkRemove}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {fields.map((rhfField, index) => {
          const isExpanded = !compactMode || expandedRows.has(rhfField.id);
          const isSelected = selectedIds.has(rhfField.id);
          const toggle = () => {
            setExpandedRows((prev) => {
              const next = new Set(prev);
              if (next.has(rhfField.id)) next.delete(rhfField.id); else next.add(rhfField.id);
              return next;
            });
          };
          // Summary-row mode: one tight card per instance, summary fields
          // shown read-only. Click anywhere on the header to expand.
          if (!isExpanded) {
            return (
              <RepeatableGroupSummaryRow
                key={rhfField.id}
                groupKey={groupKey}
                instanceIndex={index}
                groupFriendlyName={group.friendly_name}
                summaryChildren={summaryChildren}
                onExpand={toggle}
                onRemove={!disabled && group.is_repeatable && !atMin ? () => remove(index) : undefined}
                selected={isSelected}
                onToggleSelected={() => toggleSelected(rhfField.id)}
              />
            );
          }
          // Expanded: full form for this instance. In compact mode, render a
          // collapse chevron in the header so the user can fold it back.
          return (
            <Card key={rhfField.id} className="border-gray-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={compactMode ? toggle : undefined}
                    className={`flex items-center gap-2 text-sm font-medium text-gray-700 ${compactMode ? 'hover:text-blue-700' : 'cursor-default'}`}
                  >
                    {compactMode && <ChevronDown className="w-4 h-4 text-gray-500" />}
                    <span>{group.friendly_name} {index + 1}</span>
                  </button>
                  {!disabled && group.is_repeatable && !atMin && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {group.children.map((child) => (
                    <RepeatableGroupChild
                      key={child.field_id}
                      groupKey={groupKey}
                      instanceIndex={index}
                      child={child}
                      disabled={disabled}
                      derivation={resolveDerivation(child, dobSourceName)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!disabled && group.is_repeatable && !atMax && (
        <div className="relative inline-flex items-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              append(emptyInstance(group.children), { shouldFocus: true });
              if (compactMode) setPendingExpand(true);
            }}
            className={templates.length ? 'rounded-r-none border-r-0' : ''}
          >
            <Plus className="w-4 h-4 mr-1" /> {addLabel}
          </Button>
          {templates.length > 0 && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowTemplateMenu((v) => !v)}
                className="rounded-l-none px-2"
                title="Add from template"
                aria-expanded={showTemplateMenu}
              >
                <Sparkles className="w-4 h-4" />
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </Button>
              {showTemplateMenu && (
                <div
                  className="absolute z-20 top-full mt-1 left-0 min-w-[240px] max-w-[360px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1"
                  onMouseLeave={() => setShowTemplateMenu(false)}
                >
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Templates
                  </div>
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30"
                    >
                      <div className="font-medium text-gray-800 dark:text-gray-100 truncate">
                        {tpl.name}
                      </div>
                      {tpl.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {tpl.description}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {atMax && (
        <p className="text-xs text-gray-500">
          Maximum of {group.max_instances} {group.friendly_name.toLowerCase()} entries reached.
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Compact-mode summary row — collapsed view of a single instance
// ============================================================================

interface SummaryRowProps {
  groupKey: string;
  instanceIndex: number;
  groupFriendlyName: string;
  summaryChildren: ChildField[];
  onExpand: () => void;
  onRemove?: () => void;
  selected?: boolean;
  onToggleSelected?: () => void;
}

const RepeatableGroupSummaryRow: React.FC<SummaryRowProps> = ({
  groupKey,
  instanceIndex,
  groupFriendlyName,
  summaryChildren,
  onExpand,
  onRemove,
  selected = false,
  onToggleSelected,
}) => {
  const { control } = useFormContext();
  // Watch only the summary-column paths — far cheaper than watching the
  // whole instance object.
  const summaryValues = useWatch({
    control,
    name: summaryChildren.map((c) => `${groupKey}.${instanceIndex}.${c.name}`),
  });
  // useWatch returns array if name is array; coerce to array for safety.
  const values: any[] = Array.isArray(summaryValues) ? summaryValues : [summaryValues];

  return (
    <div
      className={`border rounded-md transition-colors ${
        selected
          ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-900/30'
          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 dark:hover:bg-blue-900/10'
      }`}
    >
      <div className="flex items-stretch">
        {onToggleSelected && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleSelected(); }}
            className="px-3 flex items-center justify-center text-gray-400 hover:text-blue-600"
            title={selected ? 'Deselect row' : 'Select row'}
            aria-pressed={selected}
          >
            {selected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={onExpand}
          className="flex-1 text-left p-3 flex items-center gap-3 min-w-0"
        >
          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
            {summaryChildren.map((c, i) => (
              <div key={c.name} className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                  {c.friendly_name}
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-100 truncate">
                  {formatSummaryValue(values[i], c.type)}
                </div>
              </div>
            ))}
            {summaryChildren.length === 0 && (
              <div className="text-sm text-gray-500 italic">
                {groupFriendlyName} {instanceIndex + 1} — click to view
              </div>
            )}
          </div>
          <span className="text-xs text-gray-500 shrink-0 hidden sm:inline">#{instanceIndex + 1}</span>
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="px-3 flex items-center text-red-500 hover:text-red-700 hover:bg-red-50"
            title="Remove this row"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

function emptyInstance(children: ChildField[]): Record<string, any> {
  const obj: Record<string, any> = {};
  for (const c of children) {
    obj[c.name] = c.type === 'multi_select' || c.type === 'checkbox' ? [] : '';
  }
  return obj;
}

// ============================================================================
// Per-instance child field
// ============================================================================

interface ChildProps {
  groupKey: string;
  instanceIndex: number;
  child: ChildField;
  disabled?: boolean;
  // When set, this child is CALCULATED — render it read-only and recompute it
  // from its sources in the same instance. See resolveChildDerivation.
  derivation?: ChildDerivation | null;
}

// A calculated child: read-only, and live-recomputed from its sources in the
// SAME instance so the value tracks what the user is typing next to it.
//
// A cross-record roll-up is displayed but not recomputed here — it needs the
// household's other people, so its value comes from the backend already derived.
const DerivedField: React.FC<{
  groupKey: string;
  instanceIndex: number;
  child: ChildField;
  rule: ComputedFormula;
  labelEl: React.ReactNode;
}> = ({ groupKey, instanceIndex, child, rule, labelEl }) => {
  const { control, setValue } = useFormContext();
  const path = `${groupKey}.${instanceIndex}.${child.name}`;
  const instancePath = `${groupKey}.${instanceIndex}`;
  const instance = useWatch({ control, name: instancePath }) as Record<string, unknown> | undefined;
  const stored = useWatch({ control, name: path });

  const crossRecord = isCrossRecord(rule);
  const computed = crossRecord ? null : evaluateSameRecord(rule, instance || {});
  // A same-record rule owns the value outright. A cross-record one shows what
  // the backend derived, so a stale stored value is never invented here.
  const shown = crossRecord ? stored : computed;

  useEffect(() => {
    if (crossRecord) return; // nothing to mirror: the value isn't computed locally
    // Mirror the derived value into form state so validation and any local
    // consumer see exactly what is on screen. The backend drops derived values
    // on save regardless, so this never persists a snapshot.
    setValue(path, computed ?? '', { shouldDirty: false, shouldValidate: false });
  }, [computed, crossRecord, path, setValue]);

  const isList = Array.isArray(shown);
  return (
    <div>
      {labelEl}
      <Input
        type={!isList && child.type === 'int' ? 'number' : 'text'}
        readOnly
        value={shown == null || shown === '' ? '' : isList ? formatDerived(shown) : String(shown)}
        placeholder="—"
        className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
        aria-readonly="true"
        title={describeRule(rule)}
      />
    </div>
  );
};

// Exported for unit tests (renders a single group child by com_fields type).
export const RepeatableGroupChild: React.FC<ChildProps> = ({
  groupKey,
  instanceIndex,
  child,
  disabled,
  derivation,
}) => {
  const { register, control } = useFormContext();
  const path = `${groupKey}.${instanceIndex}.${child.name}`;

  const required = child.mandatory;
  const labelEl = (
    <Label className="text-sm">
      {child.friendly_name}
      {required && <span className="text-red-500 ml-1">*</span>}
    </Label>
  );

  // TRUS-578: the legacy name heuristic (source 'heuristic', not an admin-declared
  // rule) must not LOCK an "age" child when its DOB sibling has no value for this
  // row — otherwise an age can never be entered without a date of birth. While the
  // row's DOB is empty, fall through to an editable input so the user can type the
  // age; the backend stores it, because this field has no computed_formula and
  // derived_fields only drops/recomputes configured ones. Once a DOB is present the
  // field derives again (auto-filled, read-only), preserving the TRUS-226 behaviour.
  // A CONFIGURED rule (source 'rule') is an explicit admin choice and stays locked.
  const heuristicDobName =
    derivation?.source === 'heuristic' ? (derivation.rule.inputs?.[0] as string | undefined) : undefined;
  const dobForRow = useWatch({
    control,
    name: heuristicDobName ? `${groupKey}.${instanceIndex}.${heuristicDobName}` : path,
  });
  const heuristicAwaitingDob = !!heuristicDobName && parseDateValue(dobForRow) === null;

  // Calculated child: read-only, value owned by its rule (TRUS-392). Checked
  // before every type branch below so a derived field never renders as an
  // editable input for its underlying type.
  if (derivation?.rule && !heuristicAwaitingDob) {
    return (
      <DerivedField
        groupKey={groupKey}
        instanceIndex={instanceIndex}
        child={child}
        rule={derivation.rule}
        labelEl={labelEl}
      />
    );
  }

  // NESTED REPEATABLE: child is a placeholder pointing at a sub-group.
  // Recursively render another RepeatableGroupRenderer scoped under the
  // current instance's path. react-hook-form's useFieldArray handles the
  // nested path (e.g. care_plan_goal.0.cp_goal_actions.2.cpa_task).
  if (child.type === 'field_group' && (child as any).target_field_group_id) {
    return (
      <div className="md:col-span-2 border-l-2 border-blue-200 dark:border-blue-800 pl-3 mt-1">
        <RepeatableGroupRenderer
          // Synthesize a field schema for the nested group placeholder.
          // groupKey becomes the dotted path so storage nests correctly.
          field={{
            field_id: `${groupKey}.${instanceIndex}.${child.name}`,
            field_type: 'field_group',
            label: child.friendly_name,
            field_group_id: (child as any).target_field_group_id,
          } as any}
          disabled={disabled}
        />
      </div>
    );
  }

  // Map com_fields type → input type. Mirrors FormInstanceEditor's renderField
  // semantics so behavior matches when fields are used outside a group.
  switch (child.type) {
    case 'int':
      return (
        <div>
          {labelEl}
          <Input
            type="number"
            disabled={disabled}
            {...register(path, { required })}
          />
        </div>
      );

    case 'date':
    case 'fixed_date':
      return (
        <div>
          {labelEl}
          <Input type="date" disabled={disabled} {...register(path, { required })} />
        </div>
      );

    case 'datetime':
      return (
        <div>
          {labelEl}
          <Input
            type="datetime-local"
            disabled={disabled}
            {...register(path, { required })}
          />
        </div>
      );

    case 'time':
      return (
        <div>
          {labelEl}
          <Input type="time" disabled={disabled} {...register(path, { required })} />
        </div>
      );

    case 'bool':
    case 'toggle':
      return (
        <div className="flex items-center gap-2">
          <Controller
            name={path}
            control={control}
            render={({ field }) => (
              <input
                type="checkbox"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                disabled={disabled}
              />
            )}
          />
          {labelEl}
        </div>
      );

    case 'select':
    case 'radio': {
      const opts = normalizeOptions(child.field_options);
      return (
        <div>
          {labelEl}
          <Controller
            name={path}
            control={control}
            rules={{ required }}
            render={({ field }) => (
              <select
                value={field.value ?? ''}
                onChange={field.onChange}
                disabled={disabled}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="">-- Select --</option>
                {opts.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
      );
    }

    case 'multi_select':
    case 'checkbox': {
      // Multi-value fields store an array (see the empty-instance seed and the
      // summary formatter, which both treat these as arrays). Without this case
      // they fell through to the text input below, so a configured multi-select
      // group child (e.g. MAUI's Need Type in Recovery Goal Status) rendered as a
      // blank free-text box with no options — TRUS-381. Render a checkbox group
      // bound to the array, mirroring the member profile's multi_select control.
      const opts = normalizeOptions(child.field_options);
      return (
        <div>
          {labelEl}
          <Controller
            name={path}
            control={control}
            rules={{ required }}
            render={({ field }) => {
              const selected: string[] = Array.isArray(field.value)
                ? field.value
                : field.value != null && field.value !== ''
                  ? [field.value]
                  : [];
              return (
                <div className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded border px-2 py-1">
                  {opts.length === 0 && (
                    <span className="text-xs text-gray-400">No options configured</span>
                  )}
                  {opts.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selected.includes(opt.value)}
                        disabled={disabled}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...selected, opt.value]
                            : selected.filter((v) => v !== opt.value);
                          field.onChange(next);
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              );
            }}
          />
        </div>
      );
    }

    default: {
      // TRUS-579: long text — an explicit long_text child, or a str child whose
      // str_len exceeds 255 — renders as a multi-line textarea so the whole value
      // is visible (e.g. "Grant Notes"). This mirrors the member profile inline
      // editor's isLongText rule. A short str still renders as a single-line input.
      const isLongText =
        child.type === 'long_text' || (child.type === 'str' && (child.str_len ?? 0) > 255);
      if (isLongText) {
        return (
          <div>
            {labelEl}
            <Textarea
              rows={4}
              disabled={disabled}
              className="min-h-[96px]"
              {...register(path, { required })}
            />
          </div>
        );
      }
      // str and any unknown type → text input
      return (
        <div>
          {labelEl}
          <Input
            type="text"
            disabled={disabled}
            {...register(path, { required })}
          />
        </div>
      );
    }
  }
};

export default RepeatableGroupRenderer;
